import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { parseFirestoreDate } from '../../lib/dateUtils';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function Attendance({ user }) {
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filtering, setFiltering] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get user's churches first to validate
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          setLoading(false);
          return;
        }

        // Get selected church from localStorage and validate it
        const stored = localStorage.getItem('selectedChurch');
        let church;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          // Verify the stored church is in the user's churches
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            church = storedChurch;
          } else {
            // Invalid church, use first available
            localStorage.removeItem('selectedChurch');
            church = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(church));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          // No stored church, use first one
          church = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(church));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(church);

        // Fetch attendance records without date filter initially
        await fetchAttendance(church.id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  const fetchAttendance = async (churchId, date = null) => {
    try {
      setFiltering(true);
      // Use the same date as both start and end date for single date filtering
      const records = await apiClient.getAttendance(churchId, date || null, date || null, user.id, user.role);
      setAttendanceRecords(records);
    } catch (err) {
      setError(err.message);
    } finally {
      setFiltering(false);
    }
  };

  const handleFilter = () => {
    if (!selectedChurch) return;
    fetchAttendance(selectedChurch.id, filterDate || null);
  };

  const handleClearFilter = () => {
    setFilterDate('');
    if (selectedChurch) {
      fetchAttendance(selectedChurch.id, null);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = parseFirestoreDate(timestamp);
    if (!date || isNaN(date.getTime())) return 'N/A';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error || !selectedChurch) {
    return (
      <Box>
        <Alert severity="error">{error || 'Church not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 600,
          mb: 3,
          color: '#2C2C2C',
        }}
      >
        My Attendance
      </Typography>

      {/* Date Filter */}
      <Paper sx={{ p: 3, mb: 3, maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Filter by Date
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Select Date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleFilter}
                disabled={filtering}
                fullWidth
                sx={{
                  backgroundColor: '#d4af39',
                  '&:hover': { backgroundColor: '#8B6F47' },
                }}
              >
                {filtering ? <CircularProgress size={20} /> : 'Filter'}
              </Button>
              {filterDate && (
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                  sx={{
                    borderColor: '#d4af39',
                    color: '#d4af39',
                    '&:hover': { borderColor: '#8B6F47', backgroundColor: 'rgba(168, 139, 91, 0.04)' },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Attendance Table */}
      {attendanceRecords.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No attendance records found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Check-in Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Check-in Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceRecords.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {formatDateDisplay(record.checkInDate)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {formatTime(record.checkInTime)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default Attendance;

